// SEP-2 / E1 — ManageCRM: the owner's customer-relationship console (SHARED with Vertical B), built on
// the E0 primitives (ManageEntity + EntityFormSheet + DestructiveActionGuard). Lives in the creator App
// Detail (owner side), NEVER in the customer app — contacts + deals are owner-only business data. The
// owner can add / edit / archive contacts (name, company, email, phone, relationship stage, tags,
// notes) and deals (title, pipeline stage, value, linked contact, close date, notes). Every write hits
// the ownsApp-gated /owner/{contacts,deals} routes (namespaced crm-contacts / crm-deals in a composite,
// resolved by isOwnerManagedCollection). The list is exactly the server's rows.
//
// HONEST SERVER-STATE NOTES (surfaced, never faked):
//   • Contact relationship is stored under `relationship` (a normal mutable data field) so it can be
//     changed after creation — the contract's default statusField ('status') is lifecycle-skipped by the
//     owner PATCH, so a field literally named `status` would be immutable; `relationship` avoids that.
//   • Deal `stage` IS the contract statusField but deals have no lifecycle, so the current owner route
//     sets it at CREATE and does not accept a post-create change. This console therefore lets the owner
//     pick the stage when adding a deal and shows an honest note that moving deals through the pipeline
//     after creation needs the workspace's deal-stage support (a coordinated server increment) — it does
//     NOT ship a stage button that silently no-ops.
import { useMemo } from 'preact/hooks';
import type { JSX } from 'preact';
import { Users, Handshake, Mail, Phone, Building2, Info, Activity, Clock } from 'lucide-preact';
import { getLifecycle } from '@vibeshell/shared';
import { t, getLocale } from '../lib/i18n';
import { ManageEntity } from './ManageEntity';
import type { EntityActions, EntityRow } from './ManageEntity';
import type { EntityField } from './EntityFormSheet';
import { DestructiveActionGuard } from './DestructiveActionGuard';
import { StateTransitionBar } from './StateTransitionBar';
// TRACK C — the deal-stage lifecycle + activity record live in shared modules. DEAL_LIFECYCLE is the
// CANONICAL lifecycle object (owned by status-lifecycle.ts, the registry layer); DEAL_DANGER_STAGES is a
// CRM-UI presentation choice (owned by crm-deal-lifecycle.ts). Imported by file path (not the
// `@vibeshell/shared` barrel) because the package exports map has no `./*` wildcard.
import { DEAL_LIFECYCLE } from '../../../../packages/shared/src/status-lifecycle';
import { DEAL_DANGER_STAGES } from '../../../../packages/shared/src/crm-deal-lifecycle';
import {
  normalizeCrmActivity,
  crmActivityTitleKey,
  summarizeCrmActivity,
  CRM_ACTIVITY_COLLECTION,
  type CrmActor,
  type CrmActivityType,
} from '../../../../packages/shared/src/crm-activity';

interface Contact {
  id: string;
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
  relationship?: string;
  tags?: string[] | string;
  notes?: string;
  archived?: boolean;
  [k: string]: unknown;
}
interface Deal {
  id: string;
  title?: string;
  stage?: string;
  value?: number;
  contactId?: string;
  closeDate?: string;
  notes?: string;
  archived?: boolean;
  [k: string]: unknown;
}
/** A row from the owner activity log — the raw server shape (normalized honestly at render time). */
interface ActivityRow extends EntityRow {
  type?: string;
  actor?: unknown;
  at?: number;
  ref?: unknown;
  from?: string;
  to?: string;
  note?: string;
}

const RELATIONSHIPS = ['Lead', 'Prospect', 'Customer', 'Churned'] as const;
const STAGES = ['Lead', 'Qualified', 'Proposal', 'Won', 'Lost'] as const;

const CONTACT_FIELDS: readonly EntityField[] = [
  { name: 'name', labelKey: 'shop.crm.cName', type: 'text', required: true, placeholderKey: 'shop.crm.cNamePh' },
  { name: 'company', labelKey: 'shop.crm.cCompany', type: 'text' },
  { name: 'email', labelKey: 'shop.crm.cEmail', type: 'text' },
  { name: 'phone', labelKey: 'shop.crm.cPhone', type: 'text' },
  { name: 'relationship', labelKey: 'shop.crm.cRelationship', type: 'select', options: RELATIONSHIPS.map((r) => ({ value: r, labelKey: `shop.crm.rel.${r}` })) },
  { name: 'tags', labelKey: 'shop.crm.cTags', type: 'chips', placeholderKey: 'shop.crm.cTagsPh' },
  { name: 'notes', labelKey: 'shop.crm.cNotes', type: 'textarea' },
];

const DEAL_FIELDS: readonly EntityField[] = [
  { name: 'title', labelKey: 'shop.crm.dTitle', type: 'text', required: true, placeholderKey: 'shop.crm.dTitlePh' },
  { name: 'stage', labelKey: 'shop.crm.dStage', type: 'select', required: true, options: STAGES.map((s) => ({ value: s, labelKey: `shop.crm.stage.${s}` })), helpKey: 'shop.crm.dStageHelp' },
  { name: 'value', labelKey: 'shop.crm.dValue', type: 'price', min: 0 },
  { name: 'contactId', labelKey: 'shop.crm.dContact', type: 'text', helpKey: 'shop.crm.dContactHelp' },
  { name: 'closeDate', labelKey: 'shop.crm.dCloseDate', type: 'text' },
  { name: 'notes', labelKey: 'shop.crm.dNotes', type: 'textarea' },
];

function relBadge(rel?: string): JSX.Element | null {
  if (!rel) return null;
  const cls = rel === 'Customer' ? 'pf-badge--ok' : rel === 'Prospect' ? 'pf-badge--warn' : rel === 'Churned' ? 'pf-badge--err' : '';
  const key = `shop.crm.rel.${rel}`;
  const label = t(key);
  return <span className={`pf-badge ${cls}`}>{label === key ? rel : label}</span>;
}
function stageBadge(stage?: string): JSX.Element | null {
  if (!stage) return null;
  const cls = stage === 'Won' ? 'pf-badge--ok' : stage === 'Lost' ? 'pf-badge--err' : 'pf-badge--seed';
  const key = `shop.crm.stage.${stage}`;
  const label = t(key);
  return <span className={`pf-badge ${cls}`}>{label === key ? stage : label}</span>;
}
/** A per-type icon for an activity row — category-grouped, no per-type icon explosion. */
function activityIcon(type: CrmActivityType): JSX.Element {
  if (type.startsWith('deal_')) return <Handshake size={15} />;
  if (type.startsWith('contact_')) return <Users size={15} />;
  if (type === 'note_added') return <Info size={15} />;
  return <Activity size={15} />;
}
/** An HONEST actor label: the recorded display name when present, else the localized actor kind —
 *  never a fabricated name. */
function actorLabel(actor: CrmActor): string {
  if (actor.label) return actor.label;
  const key = `shop.crm.actor.${actor.kind}`;
  const l = t(key);
  return l === key ? actor.kind : l;
}

export function ManageCRM({ appId, prefix = '' }: { appId: string; prefix?: string }): JSX.Element {
  const locale = getLocale();
  const money = (v?: number): string => {
    if (typeof v !== 'number' || Number.isNaN(v)) return '';
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(v); } catch { return `$${v}`; }
  };

  const renderContact = useMemo(() => (row: Contact, a: EntityActions<Contact>): JSX.Element => {
    const archived = !!row.archived;
    return (
      <div style={cardStyle}>
        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{row.name || t('shop.crm.unnamed')}</span>
            {relBadge(row.relationship)}
            {archived && <span className="pf-badge pf-badge--seed">{t('own.archived')}</span>}
          </span>
          <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--fg-muted)' }}>
            {row.company && <span style={chip}><Building2 size={11} /> {row.company}</span>}
            {row.email && <span style={chip}><Mail size={11} /> {row.email}</span>}
            {row.phone && <span style={chip}><Phone size={11} /> {row.phone}</span>}
          </span>
          {Array.isArray(row.tags) && row.tags.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>{row.tags.map((tg) => <span key={tg} className="pf-badge">{tg}</span>)}</span>
          )}
        </div>
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
          {!archived && <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" disabled={a.busyId === row.id} onClick={() => a.edit(row)}>{t('own.edit')}</button>}
          <DestructiveActionGuard entityLabel={row.name || t('shop.crm.thisContact')} archived={archived} busy={a.busyId === row.id} onArchive={() => a.archive(row)} onRestore={() => a.restore(row)} onDelete={() => a.remove(row)} />
        </span>
      </div>
    );
  }, [locale]);

  const renderDeal = useMemo(() => (row: Deal, a: EntityActions<Deal>): JSX.Element => {
    const archived = !!row.archived;
    // The deal's current pipeline stage; a missing/blank stage falls back to the lifecycle initial so
    // the transition control still derives valid next stages instead of rendering nothing.
    const stage = typeof row.stage === 'string' && row.stage ? row.stage : DEAL_LIFECYCLE.initial;
    return (
      <div style={stackCardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{row.title || t('shop.crm.untitledDeal')}</span>
              {stageBadge(row.stage)}
              {typeof row.value === 'number' && <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{money(row.value)}</span>}
              {archived && <span className="pf-badge pf-badge--seed">{t('own.archived')}</span>}
            </span>
            <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--fg-muted)' }}>
              {row.contactId && <span>{t('shop.crm.dContact')}: {row.contactId}</span>}
              {row.closeDate && <span>{row.closeDate}</span>}
            </span>
          </div>
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            {!archived && <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" disabled={a.busyId === row.id} onClick={() => a.edit(row)}>{t('own.edit')}</button>}
            <DestructiveActionGuard entityLabel={row.title || t('shop.crm.thisDeal')} archived={archived} busy={a.busyId === row.id} onArchive={() => a.archive(row)} onRestore={() => a.restore(row)} onDelete={() => a.remove(row)} />
          </span>
        </div>
        {/* E0 StateTransitionBar over the deal pipeline — buttons are DERIVED from the shared deal
            lifecycle's allowedNext (valid-only: a Lead offers Qualified/Lost, never Won), so an illegal
            stage jump is never offered. A pick PATCHes { status } to the owner route (setStatus, which
            NEVER fakes success — the row changes only on a 2xx). Terminal Won/Lost render no bar. */}
        {!archived && (
          <StateTransitionBar
            lifecycle={DEAL_LIFECYCLE}
            status={stage}
            busy={a.busyId === row.id}
            entityLabel={row.title || t('shop.crm.thisDeal')}
            dangerStates={DEAL_DANGER_STAGES}
            i18nPrefix="shop.crm.stage"
            onTransition={(to) => void a.setStatus(row.id, to)}
          />
        )}
      </div>
    );
  }, [locale]);

  // The owner activity timeline — every row is a REAL, server-persisted activity, normalized honestly
  // (a malformed row is shown as an honest "unrecognized" line, never a fabricated event). No writes.
  const renderActivity = useMemo(() => (row: ActivityRow): JSX.Element => {
    const act = normalizeCrmActivity(row);
    if (!act) {
      return (
        <div style={{ ...cardStyle, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{t('shop.crm.actUnknown')}</span>
        </div>
      );
    }
    const key = crmActivityTitleKey(act);
    const localized = t(key);
    const headline = localized === key ? summarizeCrmActivity(act) : localized;
    let when = '';
    try { when = new Date(act.at).toLocaleString(locale); } catch { when = ''; }
    return (
      <div style={{ ...cardStyle, alignItems: 'center' }}>
        <span className="pf-bubble" aria-hidden="true" style={{ width: 34, height: 34, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{activityIcon(act.type)}</span>
        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{headline}</span>
          <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--fg-muted)' }}>
            <span>{actorLabel(act.actor)}</span>
            {act.ref?.label && <span>· {act.ref.label}</span>}
            {act.note && <span>· {act.note}</span>}
          </span>
        </div>
        {when && (
          <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Clock size={11} aria-hidden="true" /> {when}
          </span>
        )}
      </div>
    );
  }, [locale]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ManageEntity<Contact>
        appId={appId}
        collection={`${prefix}contacts`}
        titleKey="shop.crm.contactsTitle"
        icon={<Users size={15} />}
        emptyTitleKey="shop.crm.contactsEmptyT"
        emptyBodyKey="shop.crm.contactsEmptyS"
        emptyIcon={<Users size={22} />}
        fields={CONTACT_FIELDS}
        creatable
        addLabelKey="shop.crm.addContact"
        formTitleAddKey="shop.crm.addContactTitle"
        formTitleEditKey="shop.crm.editContactTitle"
        newDefaults={{ relationship: 'Lead' }}
        csvExport
        renderItem={renderContact}
        errorAuthKey="shop.crm.errAuth"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ManageEntity<Deal>
          appId={appId}
          collection={`${prefix}deals`}
          titleKey="shop.crm.dealsTitle"
          icon={<Handshake size={15} />}
          emptyTitleKey="shop.crm.dealsEmptyT"
          emptyBodyKey="shop.crm.dealsEmptyS"
          emptyIcon={<Handshake size={22} />}
          fields={DEAL_FIELDS}
          creatable
          addLabelKey="shop.crm.addDeal"
          formTitleAddKey="shop.crm.addDealTitle"
          formTitleEditKey="shop.crm.editDealTitle"
          newDefaults={{ stage: 'Lead' }}
          csvExport
          renderItem={renderDeal}
          errorAuthKey="shop.crm.errAuth"
        />
        {/* HONEST server-state note — a deal stage is advanced via the buttons above, but PERSISTING a
            post-create move needs the `deal` lifecycle wired into the shared registry AND the deals
            contract. Shown ONLY until `getLifecycle('deal')` resolves (i.e. the integrator has wired it);
            once wired the pipeline is live server-side and the note disappears — never a stale claim. */}
        {!getLifecycle('deal') && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--fg-subtle)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Info size={12} aria-hidden="true" /> {t('shop.crm.dStageNote')}
          </p>
        )}
      </div>

      {/* Owner activity timeline — REAL rows from the append-only activity log (namespaced crm-activity
          under the CRM composite). Honest four-states via ManageEntity (loading / real rows / honest
          empty / unavailable); never a fabricated event. Read-only: activities are appended server-side
          by the authoritative append handler, not synthesized in the client. */}
      <ManageEntity<ActivityRow>
        appId={appId}
        collection={`${prefix}${CRM_ACTIVITY_COLLECTION}`}
        titleKey="shop.crm.activityTitle"
        icon={<Activity size={15} />}
        emptyTitleKey="shop.crm.activityEmptyT"
        emptyBodyKey="shop.crm.activityEmptyS"
        emptyIcon={<Activity size={22} />}
        archiveFilter={false}
        csvExport
        renderItem={renderActivity}
        errorAuthKey="shop.crm.errAuth"
      />
    </div>
  );
}

const cardStyle: JSX.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12,
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
};
// Column variant for a deal card — the top row (title/actions) plus the stage transition bar beneath.
const stackCardStyle: JSX.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, padding: 12,
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
};
const chip: JSX.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4 };
